// YOJO-2372 — pure derivation of TestAuth's effective state from the URL query
// string. Extracted from TestAuth.tsx so the auto-login path can derive the
// requested role(s) DIRECTLY from the query param instead of racing React state
// (the `role` state used to initialise to 'admin' and the auto-login effect
// fired before the mount effect applied `?role=`, minting admin for everyone).

export type RoleType = 'admin' | 'nurse' | 'content' | 'developer' | 'analyst' | 'operations' | 'sales' | 'growth';

export const VALID_ROLES: RoleType[] = ['admin', 'nurse', 'content', 'developer', 'analyst', 'operations', 'sales', 'growth'];

export interface TestAuthParams {
  /** Effective roles to send to the backend (always at least one). */
  roles: RoleType[];
  /** The primary/first role — drives the single-mode <select>. */
  primaryRole: RoleType;
  /** True when `?roles=` supplied at least one valid role (multi-role mode). */
  multiMode: boolean;
  /** True when `?auto=true` — Playwright auto-login. */
  autoLogin: boolean;
  /** The `?email=` override, or null when the caller should auto-generate one. */
  emailParam: string | null;
  /** Label for the auto-generated email when no `?email=` is given. */
  emailLabel: string;
}

const isValidRole = (r: string): r is RoleType => VALID_ROLES.includes(r as RoleType);

/**
 * Derive TestAuth's effective state from a URL query string (e.g.
 * `window.location.search`). Pure and synchronous — no React state, no race.
 *
 * `?roles=` (comma-separated) activates multi-role mode and takes precedence
 * over `?role=`. Invalid entries are dropped; if nothing valid remains it falls
 * back to single admin mode (matching the historical default).
 */
export function deriveTestAuthState(search: string): TestAuthParams {
  const params = new URLSearchParams(search);
  const emailParam = params.get('email');
  const rolesParam = params.get('roles');
  const roleParam = params.get('role');
  const autoLogin = params.get('auto') === 'true';

  // ?roles= — multi-role mode, backward compatible with a single valid entry.
  if (rolesParam) {
    const parsed = rolesParam
      .split(',')
      .map((r) => r.trim())
      .filter(isValidRole);
    if (parsed.length > 0) {
      return {
        roles: parsed,
        primaryRole: parsed[0],
        multiMode: true,
        autoLogin,
        emailParam,
        emailLabel: parsed.length === 1 ? parsed[0] : `multi-${parsed.length}`,
      };
    }
  }

  // ?role= — single-role mode; unknown/absent falls back to admin.
  const primaryRole: RoleType = roleParam && isValidRole(roleParam) ? roleParam : 'admin';
  return {
    roles: [primaryRole],
    primaryRole,
    multiMode: false,
    autoLogin,
    emailParam,
    emailLabel: primaryRole,
  };
}
